import {
  all,
  call,
  cancelled,
  debounce,
  delay,
  fork,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import {
  setCollectionPreOrderTimes,
  setDeliveryPreOrderTimes,
} from '../../actions/Actions';
import * as analytics from '../../analytics';
import { PickupLocationType, SubmitOrderResponseDm } from '../../Api';
// import { Item as OldBasketItem } from '../../BasketControl';
import { basketLogger } from '../../logger';
import { basketActions } from '../../redux';
import { basketSelectors, menuSelectors } from '../../selectors';
import {
  flagService,
  orderApiServices,
  payPalClient,
  storageServices,
  storeApiServices,
  stripeClient,
} from '../../services';
import {
  AddToBasket,
  ClearBasket,
  EditChefNotes,
  EditPickupLocationTypes,
  EditTableNumbers,
  RemoveFromBasket,
  SetRequestedTime,
  SetRestaurantCollection,
  SetRestaurantDelivery,
  SetTip,
  SetVoucher,
} from '../../Store';
import { tokenize } from '../../utils';
import { paymentActions, paymentSelectors } from '../payment';
import { routerActions } from '../router';
import * as userActions from '../user/actions';
import * as actions from './actions';
import { defaultBasketState } from './reducers';
import * as selectors from './selectors';

//#region CONFIG
const CONFIG = {
  /** debounce milliseconds */
  'basket.orderCreation.timeout': 1200,
  /** debounce milliseconds (use 0 to disable) */
  'basket.caching.timeout': 2000,
};
//#endregion

//#region HELPERS
const uniqueItemId = ({ itemId, options }: WebOrder.State.BasketItem) =>
  JSON.stringify({
    [itemId]: options,
  });
// const uniqueItemIdFromOldBasket = ({ Item, Options }: OldBasketItem) =>
//   JSON.stringify({
//     [Item.MenuItemId]: Options.map((o) => o.MenuItemOptionSetItemId),
//   });
//#endregion

//#region SAGAS

type FindRestaurantProps = {
  type: 'collection' | 'delivery';
  vrid: string;
  id: string;
};
type RestaurantSummary = ReturnType<typeof menuSelectors.getRestaurantSummary>;

/**
 * Finds Restaurant Summary using state & selector.
 * If no Restaurant in state it will query state every second until resolved.
 * @param props
 */
function* findRestaurantSummary({ type, vrid, id }: FindRestaurantProps) {
  const summaryProps = {
    deliveryType: type,
    vrId: vrid,
    prId: type === 'collection' ? id : undefined,
    deliveryLocationId: type === 'delivery' ? id : undefined,
  };

  let restaurant = menuSelectors.getRestaurantSummary(
    yield select(),
    summaryProps,
  );
  if (!restaurant) {
    let canContinue = false;
    while (!canContinue) {
      basketLogger.debug('FIND_RESTAURANT WAITING...');
      // TODO should wait max 30 seconds and if nothing happening try to download it!?
      yield delay(1000);
      // another approach would be to listen for state RestaurantSummaries updates
      // yield take(['SET_RESTAURANT_COLLECTION', 'SET_RESTAURANT_DELIVERY']);

      restaurant = menuSelectors.getRestaurantSummary(
        yield select(),
        summaryProps,
      );
      if (restaurant) {
        basketLogger.debug('FIND_RESTAURANT FOUND', {
          vrId: restaurant.VirtualRestaurantId,
          prId: restaurant.PhysicalRestaurantId,
          menuId: restaurant.MenuId,
        });
        canContinue = true;
      }
    }
  }
  return restaurant;
}

/**
 * Setup basket state
 * 1. if basket.menuId === route.menuId do nothing
 * 2. if basket storage contains cached basket setup state
 * 3. if basket in state is from different menu reset state
 * @param menuId
 */
function* setupBasketState(props: FindRestaurantProps) {
  const restaurant: RestaurantSummary = yield call(
    findRestaurantSummary,
    props,
  );

  if (!restaurant) {
    basketLogger.error(`SETUP_BASKET NO_RESTAURANT`);
    return;
  }

  const basket = selectors.getBasket(yield select());
  if (basket && basket.menuId === restaurant.MenuId) {
    // nothing to do here... already setup
    return;
  }

  yield put(
    actions.setStatus({
      status: 'loading',
    }),
  );

  const paymentId = selectors.getSelectedPaymentIdOrDefault(yield select());
  basketLogger.debug(`SETUP_BASKET PAYMENT_ID`, paymentId);

  const cachedBasked: ReturnType<typeof storageServices.getBasket> = yield call(
    storageServices.getBasket,
    restaurant.MenuId,
  );

  // RESET TO DEFAULT
  let pickupLocation: PickupLocationType;
  if ('PickupLocationTypes' in restaurant) {
    pickupLocation = restaurant.PickupLocationTypes[0];
  }

  if (cachedBasked) {
    basketLogger.debug(`SETUP_BASKET FROM_CACHE`);
    if (
      'pickupLocation' in cachedBasked &&
      'PickupLocationTypes' in restaurant &&
      restaurant.PickupLocationTypes.includes(cachedBasked.pickupLocation)
    ) {
      pickupLocation = cachedBasked.pickupLocation;
    }

    yield put(
      actions.setBasket({
        ...cachedBasked,
        status: 'loaded',
        createdOrders: {},
        validationErrors: [],
        paymentId,
        pickupLocation,
        tableServiceCategory: restaurant.TableServiceCatagory,
      }),
    );
    return;
  }

  basketLogger.debug(`SETUP_BASKET DEFAULT`);
  yield put(
    actions.setBasket({
      status: 'loaded',
      menuId: restaurant.MenuId,
      items: {},
      createdOrders: {},
      validationErrors: [],
      pickupLocation,
      tableServiceCategory: restaurant.TableServiceCatagory,
      paymentId,
    }),
  );
}

function* addBasketItem(payload: WebOrder.State.BasketItem) {
  const state = yield select();
  const items = selectors.getBasketItems(state);
  const uId = uniqueItemId(payload);
  const isNew = !items[uId];
  const item = items[uId] || payload;

  const count = isNew ? item.count : item.count + 1;
  const order = item.order || Object.keys(items).length + 1;

  yield put(
    actions.updateBasket({
      items: {
        ...items,
        [uId]: { ...item, count, order },
      },
    }),
  );
}

function* removeBasketItem(payload: WebOrder.State.BasketItem) {
  const state = yield select();
  const items = selectors.getBasketItems(state);
  const uId = uniqueItemId(payload);

  const { [uId]: item, ...restItems } = items;
  const count = item.count - 1;

  if (count > 0) {
    yield put(
      actions.updateBasket({
        items: {
          ...restItems,
          [uId]: { ...item, count },
        },
      }),
    );
  } else {
    // We need to re-index - When we remove item from basket
    const nextItems = Object.entries(restItems)
      .sort((a, b) => a[1].order - b[1].order)
      .reduce<ReturnType<typeof selectors.getBasketItems>>(
        (agg, [key, item], index) => {
          agg[key] = { ...item, order: index + 1 };
          return agg;
        },
        {},
      );

    yield put(
      actions.updateBasket({
        items: nextItems,
      }),
    );
  }
}
//#endregion

//#region ROUTE CHANGE LISTENERS

/**
 * Tries (really hard) to sync basket state with route changes.
 * - if route param contains {vrid, type, id} it can do it's magic
 * - if route param contains {vrid, prid} it can do it's magic
 * - if route param contains {orderId} it can do it's magic
 * - if route param value changes (ex: type was 'collection' to 'delivery) it will setup correct basket state
 * @param param0 Router route change action
 */
export function* syncBasketWithRoute({
  payload,
}: ReturnType<typeof routerActions.updateRouterState>) {
  const { params } = payload;

  if (!params) {
    // idk where am i
    basketLogger.debug(`ROUTE_SYNC NO_PARAMS`);
    return;
  }

  if (params.vrid && params.type && params.id) {
    basketLogger.debug(`ROUTE_SYNC`, params);
    const props = {
      type: params.type!,
      vrid: params.vrid!,
      id: params.id!,
    };
    yield call(setupBasketState, props);
    return;
  }

  if (params.vrid && params.prid) {
    basketLogger.debug(`ROUTE_SYNC`, params);
    const props = {
      type: 'collection' as const,
      vrid: params.vrid!,
      id: params.prid!,
    };
    yield call(setupBasketState, props);
    return;
  }

  // TODO see do we need this
  // if (params.orderId) {
  //   basketLogger.debug(`ROUTE_SYNC`, params);
  //   basketLogger.error(new Error('not implemented'));
  //   // get menuId from fallback mechanism
  //   return;
  // }
}

const CHECKOUT_ROUTE_TESTER = /^\/checkout\//;
/**
 * Listens to route changes.
 * 1. Activates on 'checkout' route.
 * 2. Clears basket errors on next route change.
 * @param param0 Router route change action
 */
function* clearCheckoutErrorsOnUnmount({
  payload,
}: ReturnType<typeof routerActions.updateRouterState>) {
  // wait 4 'checkout' route
  if (CHECKOUT_ROUTE_TESTER.test(payload.pathname)) {
    // MOUNTED and wait for any route change
    yield take(routerActions.updateRouterState);
    // UNMOUNTED and clear basket errors
    yield put(
      actions.updateBasket({
        status: 'loaded',
        validationErrors: [],
        error: undefined,
      }),
    );
  }
}

/**
 * Replaces old restaurant PreOrder Times w new by calling new API and replacing redux state
 */
function* useNewPreOrderTimes(
  action: SetRestaurantCollection | SetRestaurantDelivery,
) {
  const state: WebOrder.State = yield select();
  const orderCapacityFeatureEnabled = basketSelectors.getOrderCapacityFeatureFlag(
    state,
  );
  if (!orderCapacityFeatureEnabled) {
    return;
  }

  const selectedRestaurant = selectors.getRestaurantDetails(state);
  const { restaurant } = action.payload;
  try {
    // Run only if on checkout page, has fetched Restaurant data and its selected restaurant
    if (
      CHECKOUT_ROUTE_TESTER.test(state.location.pathname) &&
      restaurant &&
      restaurant.status === 'success' &&
      restaurant.data.FromAPI &&
      selectedRestaurant &&
      selectedRestaurant.PhysicalRestaurantId ===
        restaurant.data.PhysicalRestaurantId
    ) {
      basketLogger.debug('PRE_ORDER_TIMES REPLACING W NEW API RESPONSE');

      const preOrderTimes = yield call(
        storeApiServices.getRestaurantPreOrderTimes,
        {
          prId: selectedRestaurant.PhysicalRestaurantId,
          deliveryType: selectedRestaurant.DeliveryType,
        },
      );

      switch (action.type) {
        case 'SET_RESTAURANT_COLLECTION': {
          yield put(
            setCollectionPreOrderTimes({
              vrId: selectedRestaurant.VirtualRestaurantId,
              prId: selectedRestaurant.PhysicalRestaurantId,
              preOrderTimes,
            }),
          );
          break;
        }

        case 'SET_RESTAURANT_DELIVERY': {
          yield put(
            setDeliveryPreOrderTimes({
              vrId: selectedRestaurant.VirtualRestaurantId,
              dlId: action.payload.dlid,
              preOrderTimes,
            }),
          );
          break;
        }
      }
    }
  } catch (error) {
    basketLogger.debug('PRE_ORDER_TIMES REPLACING ERROR', error);
  }
}

function* watchRouteChanges() {
  // take last route change and try to update basket state (cancels already running task)
  yield takeLatest(routerActions.updateRouterState, syncBasketWithRoute);

  // take first route change and block until resolved
  yield takeLeading(
    routerActions.updateRouterState,
    clearCheckoutErrorsOnUnmount,
  );

  yield takeEvery(
    ['SET_RESTAURANT_COLLECTION', 'SET_RESTAURANT_DELIVERY'],
    useNewPreOrderTimes,
  );
}
//#endregion

//#region BASKET CHANGE LISTENERS

export function* createOrderAndVoucherOnBasketChange() {
  const state: WebOrder.State = yield select();
  const orderPayload = selectors.getOrderPayload(state);
  if (!orderPayload || typeof orderPayload === 'string') {
    basketLogger.debug('BASKET_CHANGE CREATE_ORDER ERROR', orderPayload);
    return;
  }
  const uniqueKey = tokenize(orderPayload);
  const order = selectors.getCreatedOrders(state)[uniqueKey];
  const voucherCode = state.basket.voucher && state.basket.voucher.code;
  if (order) {
    if (
      (voucherCode &&
        !order.VoucherBasketItems.some((v) => v.Name === voucherCode)) ||
      (!voucherCode && order.VoucherBasketItems.length)
    ) {
      // continue
    } else {
      basketLogger.debug('BASKET_CHANGE CREATE_ORDER SKIP');
      return;
    }
  }

  yield put(actions.setOrderLoading(true));

  // WIP moving voucher thunks to sagas
  basketLogger.warn('BASKET_CHANGE YO YO YO');
}

export function* syncBasketWithStorage() {
  try {
    const basket = selectors.getBasket(yield select());

    if (!basket.menuId) {
      basketLogger.debug(`STORAGE_SYNC NO_MENU_ID`);
      return;
    }

    basketLogger.debug(`STORAGE_SYNC START`);

    // remove undefined|null
    const optional = Object.keys({
      chefNote: basket.chefNote,
      paymentId: basket.paymentId,
      pickupLocation: basket.pickupLocation,
      tableServiceCategory: basket.tableServiceCategory,
      tableNumber: basket.tableNumber,
      tip: basket.tip,
    })
      // eslint-disable-next-line eqeqeq
      .filter((key) => basket[key] == null)
      .reduce((obj, key) => {
        obj[key] = basket[key];
        return obj;
      }, {});

    yield call(storageServices.setBasket, basket.menuId, {
      menuId: basket.menuId,
      items: basket.items,
      ...optional,
    });
    basketLogger.debug(`STORAGE_SYNC END`);
  } catch (error) {
    basketLogger.debug(`STORAGE_SYNC ERROR`, error);
  }
}

//#endregion

//#region OBSOLETE ACTIONS (BRIDGE FROM OLD TO NEW WORLD)
function* addBasketItemBridge({ payload }: AddToBasket) {
  try {
    const item: WebOrder.State.BasketItem = {
      itemId: payload.Item.MenuItemId,
      options: payload.Options.map((o) => o.MenuItemOptionSetItemId),
      count: 1,
      order: 0,
    };

    yield call(addBasketItem, item);
    // Track('Orders', 'Add Item To Basket Via Checkout', {
    //   eventLabel: basketItem.Item.Name,
    //   virtualRestaurantId: params.vrid,
    //   name: basketItem.Item.Name,
    //   itemType: basketItem.Options.length === 0 ? 'simple' : 'complex',
    //   menuId: this.props.menu.MenuId
    // });
  } catch (error) {
    basketLogger.debug('ADD_ITEM ERROR', error);
  }
}
function* removeBasketItemBridge({ payload }: RemoveFromBasket) {
  try {
    const item: WebOrder.State.BasketItem = {
      itemId: payload.Item.MenuItemId,
      options: payload.Options.map((o) => o.MenuItemOptionSetItemId),
      count: 1,
      order: 0,
    };

    yield call(removeBasketItem, item);

    // Track('Orders', 'Remove Item From Basket Via Checkout', {
    //   eventLabel: basketItem.Item.Name,
    //   virtualRestaurantId: params.vrid,
    //   menuId: this.props.menu.MenuId
    // });
  } catch (error) {
    basketLogger.debug('REMOVE_ITEM ERROR', error);
  }
}
function* setRequestedTimeBridge({ payload }: SetRequestedTime) {
  yield put(actions.updateBasket({ requestedTime: payload.time }));
}
function* editChefNoteBridge({ payload }: EditChefNotes) {
  yield put(actions.updateBasket({ chefNote: payload.value }));
}
function* editTableNumberBridge({ payload }: EditTableNumbers) {
  yield put(
    actions.updateBasket({
      tableNumber: payload.value,
    }),
  );
}
function* editPickupLocationBridge({ payload }: EditPickupLocationTypes) {
  yield put(
    actions.updateBasket({
      pickupLocation: payload.value,
    }),
  );
}
function* setTipBridge({ payload }: SetTip) {
  yield put(actions.updateBasket({ tip: payload }));
}
function* setVoucherBridge({ payload }: SetVoucher) {
  const { voucher } = payload;
  switch (voucher.status) {
    // also coming from add voucher screen
    case 'valid': {
      yield put(
        actions.updateBasket({
          orderLoading: false,
          voucher: {
            code: voucher.code,
            description: voucher.items[0].Description,
            amount: voucher.items[0].Amount,
            isUserInput: voucher.isUserInput,
            taxAmount: voucher.items[0].TaxAmount,
          },
        }),
      );
      break;
    }

    case 'none': {
      yield put(
        actions.updateBasket({
          orderLoading: false,
          voucher: undefined,
        }),
      );
      break;
    }

    case 'loading':
      yield put(
        actions.updateBasket({
          orderLoading: true,
          voucher: {
            code: voucher.code,
            description:
              voucher.items && voucher.items[0] && voucher.items[0].Description,
            amount:
              (voucher.items && voucher.items[0] && voucher.items[0].Amount) ||
              0,
            isUserInput: voucher.isUserInput,
            taxAmount:
              (voucher.items &&
                voucher.items[0] &&
                voucher.items[0].TaxAmount) ||
              0,
          },
        }),
      );
      break;
  }
}
/** Called from OrderView only if no cancellation option */
function* clearBasketBridge({ payload }: ClearBasket) {
  // clear storage
  yield call(storageServices.setBasket, payload.menuId, undefined);
}

function* watchObsoleteBasketActions() {
  yield takeEvery('ADD_TO_BASKET', addBasketItemBridge);
  yield takeEvery('REMOVE_FROM_BASKET', removeBasketItemBridge);
  yield takeEvery('SET_REQUESTED_TIME', setRequestedTimeBridge);
  yield takeEvery('EDIT_CHEF_NOTES', editChefNoteBridge);
  yield takeEvery('EDIT_TABLE_NUMBERS', editTableNumberBridge);
  yield takeEvery('EDIT_PICKUP_LOCATION_TYPES', editPickupLocationBridge);
  yield takeEvery('BASKET_SET_TIP', setTipBridge);
  yield takeEvery('CLEAR_BASKET', clearBasketBridge);
  yield takeEvery('SET_VOUCHER', setVoucherBridge);

  // Listen to basket changes and update cache
  if (CONFIG['basket.caching.timeout']) {
    yield debounce(
      CONFIG['basket.caching.timeout'],
      [
        'ADD_TO_BASKET',
        'REMOVE_FROM_BASKET',
        'SET_REQUESTED_TIME',
        'EDIT_TABLE_NUMBERS',
        'EDIT_PICKUP_LOCATION_TYPES',
        'BASKET_SET_TIP',
      ],
      syncBasketWithStorage,
    );
  }
}
//#endregion

//#region CHECKOUT

function* trackCancelOrder() {
  const state = yield select();
  const total = selectors.getTotalToPay(state);
  const restaurantSummary = selectors.getRestaurantSummary(state);
  if (!restaurantSummary || !total) {
    basketLogger.debug('TRACK CANCEL_ORDER ERROR', restaurantSummary, total);
    return;
  }

  yield call(analytics.Track, 'Orders', 'Cancel Order', {
    eventLabel: restaurantSummary.PhysicalRestaurantName,
    eventValue: Math.round(total),
  });
}

export function* checkoutValidationFlow() {
  basketLogger.debug('CHECKOUT_FLOW VALIDATING');
  yield put(actions.setStatus({ status: 'validating', error: undefined }));
  while (true) {
    const checkoutErrors: ReturnType<typeof selectors.getCheckoutErrors> = yield select(
      selectors.getCheckoutErrors,
    );

    if (checkoutErrors.length) {
      basketLogger.debug('CHECKOUT_FLOW VALIDATING ERROR', checkoutErrors);
      yield put(actions.setValidationErrors(checkoutErrors));
      yield put(actions.setStatus({ status: 'loaded' }));

      const [cancel] = yield race([
        take(actions.cancelCheckoutFlow),
        take(actions.continueCheckoutFlow),
      ]);

      basketLogger.debug('CHECKOUT_FLOW VALIDATING CONTINUE');

      if (cancel) {
        basketLogger.debug('CHECKOUT_FLOW VALIDATING CANCEL');
        // terminate saga and reset state
        yield put(actions.setValidationErrors([]));
        return false;
      }
      yield put(actions.setStatus({ status: 'validating' }));
    } else {
      basketLogger.debug('CHECKOUT_FLOW VALIDATING SUCCESS');

      yield put(actions.setValidationErrors([]));
      return true;
    }
  }
}
export function* checkoutOrderFlow() {
  const state: WebOrder.State = yield select();
  const orderPayload = selectors.getOrderPayload(state);
  if (!orderPayload || typeof orderPayload === 'string') {
    // TODO shouldn't happen but find better solution
    throw new Error(`Something went wrong!`);
  }
  const uniqueKey = tokenize(orderPayload);
  let order = selectors.getCreatedOrders(state)[uniqueKey];

  let voucherCode: string;
  let isUserVoucher = false;

  const basket = selectors.getBasket(state);
  if (basket.voucher) {
    voucherCode = basket.voucher.code;
    isUserVoucher = basket.voucher.isUserInput;
  }

  if (
    order &&
    voucherCode &&
    !order.VoucherBasketItems.some((v) => v.Name === voucherCode)
  ) {
    order = undefined;
  }

  if (!order) {
    // override on user input voucher or paymentId is 0 or less (stripe & new_card)
    if (
      (voucherCode && isUserVoucher) ||
      (voucherCode && orderPayload.PaymentAccountId < 1)
    ) {
      basketLogger.info(
        'CHECKOUT_FLOW CREATING_ORDER_W_USER_VOUCHER',
        voucherCode,
      );

      // to avoid auto-apply voucher we need to do things like this
      order = yield call(orderApiServices.create, {
        ...orderPayload,
        PaymentAccountId: undefined,
      });
      yield call(orderApiServices.applyVoucher, {
        orderId: order.OrderId,
        voucherCode,
        paymentAccountId: orderPayload.PaymentAccountId,
      });
    } else {
      basketLogger.info('CHECKOUT_FLOW CREATING_ORDER');
      order = yield call(orderApiServices.create, orderPayload);
    }
  } else {
    basketLogger.info('CHECKOUT_FLOW REUSING_CACHED_ORDER');
  }
  return order;
}
export function* checkoutSubmitOrderFlow(
  order: WebOrder.State.BasketCreatedOrder,
) {
  const state = yield select();
  const orderPayload = selectors.getOrderPayload(state);
  if (!orderPayload || typeof orderPayload === 'string') {
    // TODO shouldn't happen... find better solution
    throw new Error(`Unexpected_error_occurred`);
  }
  const payment = selectors.getSelectedPaymentOrDefault(state);
  const chefNote = selectors.getChefNotes(state);

  let submittedResult: SubmitOrderResponseDm;
  switch (payment.paymentId) {
    case 'paypal': {
      basketLogger.info('CHECKOUT_FLOW PAYMENT_FLOW PAYPAL START');
      const { nonce, deviceData } = yield call(payPalClient.tokenize);

      // @ts-ignore unresolved
      submittedResult = yield call(orderApiServices.submitPayPal, {
        clientToken: nonce,
        deviceData,
        chefNote,
        orderId: order.OrderId,
      });
      break;
    }
    case 'stripe': {
      basketLogger.info('CHECKOUT_FLOW PAYMENT_FLOW STRIPE START');

      /*
        For security reasons, the PaymentRequest.show() method can't just be initiated at any time.
        It may only be called while handling events that represent user interactions,
        such as click, keyup, or the like.
        all that logic (update/show) is in StripePayButton
      */
      // const state = yield select();
      // const updatePayload = selectors.getStripePaymentRequestUpdateOptions(
      //   state
      // );
      // yield call(stripeClient.paymentRequest.update, updatePayload);
      // yield call(stripeClient.paymentRequest.show);

      basketLogger.debug('CHECKOUT_FLOW PAYMENT_FLOW STRIPE WAITING');
      const [cancel] = yield race([
        take(actions.cancelCheckoutFlow),
        take(actions.continueCheckoutFlow),
      ]);

      if (cancel) {
        basketLogger.debug('CHECKOUT_FLOW PAYMENT_FLOW STRIPE CANCEL');
        yield put(actions.setStatus({ status: 'loaded', error: undefined }));
        return;
      }
      const paymentToken = stripeClient.paymentRequest.getPaymentToken();
      if (!paymentToken) {
        throw new Error('Unexpected_payment_token_error');
      }

      submittedResult = yield call(orderApiServices.submitPayStripe, {
        stripePaymentMethodId: paymentToken,
        chefNote,
        orderId: order.OrderId,
      });

      break;
    }
    case 'new_card': {
      basketLogger.info('CHECKOUT_FLOW PAYMENT_FLOW NEW_CARD START');

      yield put(
        routerActions.navigate({
          pathname: `/card/${encodeURIComponent(window.location.hash)}`,
        }),
      );

      const [cancel, addedCardAction] = yield race([
        take(paymentActions.cancelAddBridge),
        take(paymentActions.creditCardPaymentAdded),
      ]);
      if (cancel) {
        basketLogger.info('CHECKOUT_FLOW PAYMENT_FLOW NEW_CARD CANCELLED');
        return;
      }

      const { paymentAccountId } = (addedCardAction as ReturnType<
        typeof paymentActions.creditCardPaymentAdded
      >).payload;

      yield put(actions.selectPayment({ paymentId: `${paymentAccountId}` }));

      basketLogger.info('CHECKOUT_FLOW PAYMENT_FLOW NEW_CARD SUBMITTING_ORDER');

      submittedResult = yield call(orderApiServices.submit, {
        chefNote,
        orderId: order.OrderId,
        paymentAccountId,
      });
      break;
    }
    default:
      basketLogger.info('CHECKOUT_FLOW SUBMITTING_ORDER');

      submittedResult = yield call(orderApiServices.submit, {
        chefNote,
        orderId: order.OrderId,
        paymentAccountId: orderPayload.PaymentAccountId,
      });
      break;
  }

  return submittedResult;
}
export function* checkoutUpdateDefaultPayment() {
  const state = yield select();
  const payment = selectors.getSelectedPaymentOrDefault(state);
  const payments = paymentSelectors.getPayments(state);
  if (!payment.IsDefaultPaymentMethod) {
    try {
      const newPayments = Object.entries(payments).reduce(
        (pState, [key, p]) => {
          pState[key] = {
            ...p,
            IsDefaultPaymentMethod:
              payment.PaymentAccountId === p.PaymentAccountId,
          };
          return pState;
        },
        {} as WebOrder.State.Payments['items'],
      );

      basketLogger.debug(
        'CHECKOUT_FLOW SET_DEFAULT_PAYMENT',
        payment.paymentId,
      );
      yield put(
        paymentActions.setState({
          status: 'success',
          items: newPayments,
          error: undefined,
        }),
      );

      // clear storage and get fresh data next time (the set default payment on backend is future task...)
      yield call(storageServices.setPayments, undefined);
    } catch (error) {
      basketLogger.debug('CHECKOUT_FLOW SET_DEFAULT_PAYMENT ERROR', error);
    }
  }
}
export function* checkoutBasketCleanup() {
  yield put(
    actions.updateBasket({
      ...defaultBasketState,
      chefNote: '',
      error: undefined,
      requestedTime: undefined,
      submittedOrder: undefined,
      tableNumber: undefined,
      voucher: undefined,

      // these guys should stay as they r
      // menuId: 0,
      // over18Confirmed: false,
      // paymentId: '',
      // pickupLocation: 0,
      // tip: 0
    }),
  );
}
export function* checkoutTrackPurchase(orderId: number) {
  const state = yield select();
  const total = selectors.getTotalToPay(state);
  const { items, voucher } = selectors.getBasketBridge(state);
  const taxTotal = selectors.getTaxTotal(state);
  const restaurantSummary = selectors.getRestaurantSummary(state);
  if (!orderId || !restaurantSummary) {
    return;
  }

  const { IsoCurrency, PhysicalRestaurantName } = restaurantSummary;

  const purchaseEventData = {
    affiliation: `Flipdish - order - ${PhysicalRestaurantName}`,
    transaction_id: `${orderId}`,
    value: total,
    currency: IsoCurrency,
    items: items.map((item) => ({
      id: `${item.Item.MenuItemId}`,
      name: item.Item.Name,
      price: item.Item.ActualPrice,
      quantity: 1, // TODO should group items
    })),
    coupon: voucher.code || undefined,
    tax: taxTotal || undefined,
  };

  if (flagService.isFlagOn(state, 'altGoogleAnalyticsPurchaseEvent')) {
    analytics.eCommerce.purchaseGaInvestigation(purchaseEventData);
  } else {
    analytics.eCommerce.purchase(purchaseEventData);
  }

  analytics.Track('Orders', 'Place Order', {
    event_label: PhysicalRestaurantName,
    event_value: total, // if errors starts showing in gtag debugger try using `Math.round(total)`, this was issue w analytics.js
  });
}

export function* checkoutFlow() {
  try {
    basketLogger.info('CHECKOUT_FLOW START');

    // VALIDATION STATE
    const isCheckoutValid = yield call(checkoutValidationFlow);
    if (!isCheckoutValid) {
      return;
    }

    // CREATE ORDER STATE
    yield put(actions.setStatus({ status: 'submitting' }));

    const order: WebOrder.State.BasketCreatedOrder = yield call(
      checkoutOrderFlow,
    );

    // SUBMIT ORDER STATE
    const submittedResult: SubmitOrderResponseDm = yield call(
      checkoutSubmitOrderFlow,
      order,
    );
    if (!submittedResult) {
      return;
    }

    // blocking call coz of redirect...
    // TODO find better place for this, maybe in OrderView coz order can be cancelled and stuff
    yield call(checkoutTrackPurchase, submittedResult.OrderId);

    //#region temporary needed to clear old basket localStorage on payment redirect or page refresh
    const basket: WebOrder.State.Basket = yield select(
      (state: WebOrder.State) => state.basket,
    );
    if (basket && basket.menuId) {
      storageServices.setSubmittedOrderMeta({
        orderId: submittedResult.OrderId,
        menuId: basket.menuId,
        paymentId: basket.paymentId,
      });
    }
    //#endregion

    if (submittedResult.RedirectUri) {
      basketLogger.info(
        'CHECKOUT_FLOW:REDIRECTING',
        submittedResult.RedirectUri,
      );

      window.location.href = submittedResult.RedirectUri;
      return;
    }

    yield put(actions.setSubmittedOrder(submittedResult));
    yield put(actions.setStatus({ status: 'submitted' }));

    // CONFIRM OR CANCEL ORDER STATE
    const state: WebOrder.State = yield select();
    const params = { ...state.location.params }; // don't keep ref to state object coz it will probably change on next yield
    const { concessionStoreId } = params;
    const orderSuccessRoute = concessionStoreId
      ? `/order/${order.OrderId}/${concessionStoreId}`
      : `/order/${order.OrderId}`;

    basketLogger.debug('CHECKOUT_FLOW NAVIGATING', orderSuccessRoute);
    yield put(
      routerActions.navigate({
        pathname: orderSuccessRoute,
      }),
    );

    // TODO should be race(take(cancel), take(countdown), take(continue))
    // and should use submittedResult.CancelOrderPeriodSeconds (if null than no cancel)
    const [cancel] = yield race([
      take(actions.cancelCheckoutFlow),
      take(actions.continueCheckoutFlow),
    ]);

    if (cancel) {
      try {
        basketLogger.info('CHECKOUT_FLOW CANCEL_ORDER');
        yield put(actions.setStatus({ status: 'cancelling' }));

        yield call(orderApiServices.cancel, { orderId: order.OrderId });

        yield spawn(trackCancelOrder);

        yield put(actions.setStatus({ status: 'loaded', error: undefined }));
        yield put(routerActions.navigateBack());
        return;
      } catch (error) {}
    }

    basketLogger.debug('CHECKOUT_FLOW CONTINUE');

    // RESET CANCEL TIMEOUT IN CASE USER COMES BACK
    yield put(
      actions.setSubmittedOrder({
        ...submittedResult,
        CancelOrderPeriodSeconds: 0,
      }),
    );

    // UPDATE DEFAULT PAYMENT (LOCALLY) STATE
    yield call(checkoutUpdateDefaultPayment);

    // RESET BASKET STATE
    yield call(checkoutBasketCleanup);

    // Remove item from Local Storage after order was placed
    // basket having the items from previous order when logging in
    yield call(storageServices.setBasket, basket.menuId, undefined);

    basketLogger.info('CHECKOUT_FLOW COMPLETE');
  } catch (error) {
    basketLogger.error('CHECKOUT_FLOW ERROR', error);
    const state: WebOrder.State = yield select();
    const orderPayload = selectors.getOrderPayload(state);

    const uniqueKey = tokenize(orderPayload);
    const order = selectors.getCreatedOrders(state)[uniqueKey];

    if (flagService.isFlagOn(state, 'clearAllCreatedOrersAfterError')) {
      try {
        yield call(orderApiServices.cancel, { orderId: order.OrderId });
      } catch (error) {}

      basketLogger.debug('CLEAR_ALL_CREATED_ORDERS INSIDE_ERROR');
      //this is needed cause BE or stripe can't handle the same orderId
      yield put(basketActions.clearCreatedOrders());
    }
    yield put(actions.setStatus({ status: 'error', error }));
  } finally {
    if (yield cancelled()) {
      basketLogger.info('CHECKOUT_FLOW CANCELLED');
    }
  }
}

export function* watchCheckout() {
  while (true) {
    yield take(actions.startCheckoutFlow);
    /*
        ***THIS IS A BLOCKING CALL***
        It means that watchCheckout will ignore any CHECKOUT_REQUEST event until
        the current checkout completes, either by success or by Error.
        i.e. concurrent CHECKOUT_REQUEST are not allowed
      */

    yield call(checkoutFlow);
  }
}
//#endregion

function* watchLogout() {
  while (true) {
    // WIP moving voucher thunks to sagas
    // // wait 4 user to login
    // yield take(userActions.loginSuccessBridge);

    // // listen to basket changes and create order and voucher
    // yield debounce(
    //   CONFIG['basket.orderCreation.timeout'],
    //   [
    //     'ADD_TO_BASKET',
    //     'REMOVE_FROM_BASKET',
    //     'SET_REQUESTED_TIME',
    //     'EDIT_TABLE_NUMBERS',
    //     'EDIT_PICKUP_LOCATION_TYPES',
    //     'BASKET_SET_TIP',
    //     actions.selectPayment
    //   ],
    //   createOrderAndVoucherOnBasketChange
    // );

    yield take(userActions.logoutSuccessBridge);

    // reset basket state
    yield put(actions.setBasket(defaultBasketState));
  }
}

export function* basketSaga() {
  yield all([
    fork(watchRouteChanges),
    fork(watchObsoleteBasketActions),
    fork(watchCheckout),
    fork(watchLogout),
  ]);
}
